import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Loader2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BUSINESS_TYPES_QUERY_KEY,
  listBusinessTypes,
} from '@/lib/api/business-types';

export const businessSchema = z.object({
  language: z.enum(['en', 'es']),
  businessName: z.string().min(1, 'Required'),
  // Real id from GET /business-types (repo.BusinessType.id), not a slug.
  businessTypeId: z.string().min(1, 'Required'),
});

export type BusinessValues = z.infer<typeof businessSchema>;

export function StepBusiness({
  defaultValues,
  onNext,
}: {
  defaultValues: Partial<BusinessValues>;
  onNext: (data: BusinessValues) => void;
}) {
  const { t, setLanguage } = useLanguage();

  // Static lookup of business categories — see Task 4a in the integration
  // plan. Cached under BUSINESS_TYPES_QUERY_KEY for the session.
  const businessTypesQuery = useQuery({
    queryKey: BUSINESS_TYPES_QUERY_KEY,
    queryFn: listBusinessTypes,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<BusinessValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: {
      language: defaultValues.language ?? 'en',
      businessName: defaultValues.businessName ?? '',
      businessTypeId: defaultValues.businessTypeId ?? '',
    },
  });

  const selectedLang = watch('language');
  const selectedTypeId = watch('businessTypeId');
  const activeTypes = (businessTypesQuery.data ?? []).filter(
    (type) => type.is_active,
  );

  function onSubmit(data: BusinessValues) {
    setLanguage(data.language);
    onNext(data);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <Label>{t.preferredLanguage}</Label>
        <div className="flex gap-2">
          {(['en', 'es'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                setValue('language', lang);
                setLanguage(lang);
              }}
              className={[
                'flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors',
                selectedLang === lang
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:border-primary',
              ].join(' ')}
            >
              {lang === 'en' ? t.english : t.spanish}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="businessName">{t.businessName}</Label>
        <Input
          id="businessName"
          placeholder={t.businessNamePlaceholder}
          aria-invalid={Boolean(errors.businessName)}
          {...register('businessName')}
        />
        {errors.businessName && (
          <p className="text-destructive text-xs">{t.required}</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Label>{t.businessType}</Label>

        {businessTypesQuery.isPending && (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2Icon className="w-4 h-4 animate-spin" />
          </div>
        )}

        {businessTypesQuery.isError && (
          <p className="text-destructive text-xs">
            Could not load business types. Please try again.
          </p>
        )}

        {!businessTypesQuery.isPending && !businessTypesQuery.isError && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activeTypes.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setValue('businessTypeId', type.id)}
                className={[
                  'py-3 px-4 rounded-lg border text-sm font-medium text-left transition-colors',
                  selectedTypeId === type.id
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-background border-border hover:border-primary',
                ].join(' ')}
              >
                {type.label}
              </button>
            ))}
          </div>
        )}

        {errors.businessTypeId && (
          <p className="text-destructive text-xs">{t.required}</p>
        )}
      </div>

      <Button type="submit" className="w-full mt-2">
        {t.next}
      </Button>
    </form>
  );
}
